import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useState } from "react";
import QuantumLightfield from "./QuantumLightfield";
import AgentNodes from "./AgentNodes";
import ArchitectureModal from "./ArchitectureModal";
import ExecutiveNav from "./ExecutiveNav";
import "../styles/architectureModal.css";
import { useLang } from "../lib/LangContext";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_ff176637-490c-4d8d-8821-0039665e7b20/artifacts/1ppcjvci_Web%20logo.png";

const easeLux = [0.22, 1, 0.36, 1];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24, filter: "blur(8px)" },
  animate: { opacity: 1, y: 0, filter: "blur(0px)" },
  transition: { duration: 0.9, ease: easeLux, delay },
});

const Hero = () => {
  const { lang, setLang, t } = useLang();
  const [archOpen, setArchOpen] = useState(false);
  return (
    <section
      data-testid="hero-section"
      className="km-hero"
      aria-label="Kreux Matrix — Hero"
    >
      {/* Animated background engine */}
      <div className="km-hero__bg" aria-hidden="true">
        <QuantumLightfield />
        <AgentNodes />
        <div className="km-hero__hex" />
        <div className="km-hero__scanlines" />
        <div className="km-hero__vignette" />
        <div className="km-hero__grain" />
      </div>

      {/* Top navigation */}
      <motion.header
        data-testid="hero-nav"
        className="km-nav"
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: easeLux, delay: 0.1 }}
      >
        <a href="/" className="km-nav__brand" data-testid="nav-brand">
          <span className="km-nav__logo-ring" aria-hidden="true" />
          <img
            src={LOGO_URL}
            alt="Kreux Matrix"
            className="km-nav__logo"
            data-testid="nav-logo"
          />
          <span className="km-nav__wordmark">
            <span className="km-nav__wm-1">Kreux</span>
            <span className="km-nav__wm-2">Matrix</span>
          </span>
        </a>

        <nav className="km-nav__links" data-testid="nav-links">
          <ExecutiveNav onOpenArch={() => setArchOpen(true)} />
        </nav>

        <div
          className="km-nav__lang"
          data-testid="nav-lang-toggle"
          role="group"
          aria-label={t("nav.langLabel")}
        >
          <button
            type="button"
            className={"km-nav__lang-btn" + (lang === "en" ? " is-on" : "")}
            onClick={() => setLang("en")}
            aria-pressed={lang === "en"}
            data-testid="nav-lang-en"
          >
            {t("nav.langEN")}
          </button>
          <span
            className={"km-nav__lang-thumb is-" + lang}
            aria-hidden="true"
          />
          <button
            type="button"
            className={"km-nav__lang-btn" + (lang === "de" ? " is-on" : "")}
            onClick={() => setLang("de")}
            aria-pressed={lang === "de"}
            data-testid="nav-lang-de"
          >
            {t("nav.langDE")}
          </button>
        </div>
      </motion.header>

      {/* Hero content — centered, unframed */}
      <div className="km-hero__inner">
        <div className="km-hero__stage">
          <motion.div
            className="km-eyebrow"
            data-testid="hero-eyebrow"
            {...fadeUp(0.2)}
          >
            <span className="km-eyebrow__pulse" />
            <span>{t("hero.eyebrow")}</span>
          </motion.div>

          <motion.h1
            className="km-headline"
            data-testid="hero-headline"
            {...fadeUp(0.32)}
          >
            <span className="km-headline__line">
              {t("hero.headline1pre")} <em className="km-headline__em">{t("hero.headline1em")}</em>
            </span>
            <span className="km-headline__line">
              {t("hero.headline2pre")} <span className="km-headline__cyan">{t("hero.headline2cyan")}</span>
            </span>
          </motion.h1>

          <motion.p
            className="km-sub"
            data-testid="hero-sub"
            {...fadeUp(0.48)}
          >
            {t("hero.sub.prefix")}
            <span className="km-sub__mark">{t("hero.sub.mark")}</span>
            {t("hero.sub.suffix")}
          </motion.p>

          <motion.div
            className="km-cta-row"
            data-testid="hero-cta-row"
            {...fadeUp(0.62)}
          >
            <a
              id="hero-cta"
              href="#experience"
              className="km-cta target:scale-105 target:ring-4 target:ring-[var(--il-cyan)]/80 target:shadow-[0_0_80px_rgba(var(--il-cyan-rgb),0.8)] target:animate-pulse transition-all duration-700 ease-out"
              data-testid="hero-primary-cta"
              onClick={(e) => { e.preventDefault(); setArchOpen(true); }}
              style={{ "--il-cyan-rgb": "0, 229, 255" }}
            >
              <span className="km-cta__label">{t("hero.primaryCTA")}</span>
              <span className="km-cta__icon" aria-hidden="true">
                <ArrowUpRight size={18} strokeWidth={2.25} />
              </span>
              <span className="km-cta__glow" aria-hidden="true" />
            </a>
          </motion.div>
        </div>
      </div>

      {/* Lead capture overlay — fixed-position; does NOT alter Hero layout */}
      <ArchitectureModal open={archOpen} onClose={() => setArchOpen(false)} />
    </section>
  );
};

export default Hero;
